import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Mail, Phone, Building2, Calendar } from "lucide-react";
import { adminSubmissions, adminDeleteSubmission, adminNewsletter, adminDeleteNewsletter, apiError } from "../../lib/api";
import { Btn } from "./AdminKit";

const TABS = [
  { key: "contact", label: "Contact" },
  { key: "volunteer", label: "Volunteer" },
  { key: "partner", label: "Partnership" },
  { key: "newsletter", label: "Mailing List" },
];

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

function Row({ label, value }) {
  if (!value) return null;
  return (
    <div className="text-sm">
      <span className="text-nead-navy/45">{label}: </span>
      <span className="text-nead-navy/80">{value}</span>
    </div>
  );
}

export default function AdminSubmissions() {
  const [tab, setTab] = useState("contact");
  const [subs, setSubs] = useState([]);
  const [news, setNews] = useState([]);

  const load = () => {
    adminSubmissions().then(setSubs).catch((e) => toast.error(apiError(e)));
    adminNewsletter().then(setNews).catch((e) => toast.error(apiError(e)));
  };
  useEffect(() => { load(); }, []);

  const removeSub = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    try { await adminDeleteSubmission(id); toast.success("Deleted."); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const removeNews = async (id) => {
    if (!window.confirm("Remove this subscriber?")) return;
    try { await adminDeleteNewsletter(id); toast.success("Removed."); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const filtered = subs.filter((s) => s.type === tab);
  const count = (key) => key === "newsletter" ? news.length : subs.filter((s) => s.type === key).length;

  return (
    <div>
      <h1 className="font-display font-bold text-2xl text-nead-navy">Enquiries</h1>
      <p className="mt-1 text-sm text-nead-navy/55">Everything submitted through the website forms and the mailing list signup.</p>

      <div className="mt-6 flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`submissions-tab-${t.key}`}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${tab === t.key ? "bg-nead-navy text-white" : "bg-white border border-black/10 text-nead-navy/70 hover:border-nead-pink hover:text-nead-pink"}`}
          >
            {t.label}
            <span className={`ml-2 rounded-full px-1.5 py-0.5 text-xs ${tab === t.key ? "bg-white/20" : "bg-black/5"}`}>{count(t.key)}</span>
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-4" data-testid="submissions-list">
        {tab === "newsletter" ? (
          <>
            {news.map((n) => (
              <div key={n.id || n.email} data-testid={`newsletter-${n.id}`} className="flex items-center justify-between rounded-xl border border-black/[0.07] bg-white px-5 py-4">
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-nead-navy/40" />
                  <a href={`mailto:${n.email}`} className="text-sm font-medium text-nead-navy hover:text-nead-pink">{n.email}</a>
                  <span className="text-xs text-nead-navy/45">{fmtDate(n.created_at)}</span>
                </div>
                {n.id && <Btn variant="ghost" onClick={() => removeNews(n.id)} data-testid={`newsletter-delete-${n.id}`}><Trash2 className="h-4 w-4" /></Btn>}
              </div>
            ))}
            {!news.length && <div className="rounded-2xl border border-black/[0.07] bg-white p-10 text-center text-nead-navy/40">No subscribers yet.</div>}
          </>
        ) : (
          <>
            {filtered.map((s) => (
              <div key={s.id} data-testid={`submission-${s.id}`} className="rounded-2xl border border-black/[0.07] bg-white p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <h3 className="font-display font-bold text-lg text-nead-navy">{s.organisation || s.name || "Enquiry"}</h3>
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-nead-navy/60">
                      {s.name && s.organisation && <span className="inline-flex items-center gap-1.5">{s.name}</span>}
                      {s.email && <a href={`mailto:${s.email}`} className="inline-flex items-center gap-1.5 hover:text-nead-pink"><Mail className="h-3.5 w-3.5" />{s.email}</a>}
                      {s.phone && <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />{s.phone}</span>}
                      <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />{fmtDate(s.created_at)}</span>
                    </div>
                  </div>
                  <Btn variant="ghost" onClick={() => removeSub(s.id)} data-testid={`submission-delete-${s.id}`}><Trash2 className="h-4 w-4" /></Btn>
                </div>
                <div className="mt-4 space-y-1">
                  <Row label="Subject" value={s.subject} />
                  <Row label="Interest" value={s.interest} />
                  <Row label="Availability" value={s.availability} />
                  <Row label="Partnership type" value={s.partnership_type} />
                </div>
                {s.message && <p className="mt-3 whitespace-pre-line rounded-lg bg-nead-mist p-3 text-sm text-nead-navy/70">{s.message}</p>}
              </div>
            ))}
            {!filtered.length && <div className="rounded-2xl border border-black/[0.07] bg-white p-10 text-center text-nead-navy/40">No {TABS.find((t) => t.key === tab)?.label.toLowerCase()} submissions yet.</div>}
          </>
        )}
      </div>
    </div>
  );
}
